/**
 * App.tsx —— 应用入口与路由（首页 ↔ 牌桌）
 *
 * Controller 用模块级单例：StrictMode 会把组件挂载两次，若把编排器放进 state
 * 会产生两套定时器与两个 Worker。单例 + 显式 reset 才是这里的正确做法。
 */

import { useCallback, useEffect, useState } from 'react';
import './ui/tokens.css';
import './ui/base.css';
import './ui/layout.css';
import { GameController, type SetupParams } from './state/controller';
import { applySettingsToDom } from './state/settings';
import { loadDoc, type LoadResult } from './state/persistence';
import { DEBUG_TOOLS } from './state/env';
import { exposeTelemetry, setTelemetryDebug } from './state/telemetry';
import { pushToast } from './state/toasts';
import { useGameVersion, useSettings, useToasts } from './ui/hooks';
import { ToastLayer } from './ui/primitives';
import { StartScreen } from './ui/StartScreen';
import { GameScreen } from './ui/GameScreen';
import { PwaHints } from './ui/PwaHints';
import { MpApp } from './ui/mp/MpApp';
import { mpClient, prewarmWorker } from './net/mpClient';
import { autoHealStaleBuild } from './net/appVersion';

const controller = new GameController();

// 调试入口只在开发态 / 显式诊断包里存在（PRD 8.6：生产包不得带调试能力）。
// DEBUG_TOOLS 是编译期常量，生产构建时整块会被摇树掉，产物里没有 __riverside* 符号。
if (DEBUG_TOOLS && typeof window !== 'undefined') {
  exposeTelemetry();
  setTelemetryDebug(new URLSearchParams(window.location.search).has('debug'));
  (window as unknown as Record<string, unknown>).__riversideController = controller;
}

export function App() {
  const settings = useSettings();
  const toasts = useToasts();
  useGameVersion(controller);
  const [save, setSave] = useState<LoadResult>(() => loadDoc());
  const [lastSetup, setLastSetup] = useState<SetupParams | null>(null);
  // 刷新后若存在未结束的多人会话，直接进入多人流程并自动重入房间（AC4.1 / BUG-R15-01）
  const [mp, setMp] = useState(() => mpClient.hasResumableSession());

  useEffect(() => {
    if (mpClient.hasResumableSession() && mpClient.state.conn === 'idle') {
      const ok = mpClient.resume();
      if (!ok) setMp(false);
    }
  }, []);

  /*
   * 多人对战诊断注入通道（PRD §6「可验证性」/ §9.1 依赖：diag 多人注入通道）。
   * 与 __riversideController 同样受 DEBUG_TOOLS 编译期常量门控——生产构建整块被摇树掉，
   * 产物无任何 __mp* 符号。QA 的 qa-round22 几何回归据此把 mpClient.store 直接打到目标
   * 浮层态（复用 round17 的 FakeWS 帧结构造 GameView/LobbyView），无需真起 WebSocket。
   */
  useEffect(() => {
    if (!DEBUG_TOOLS || typeof window === 'undefined') return;
    const w = window as unknown as Record<string, unknown>;
    w.__mpClient = mpClient;             // store 注入通道（mpClient.store.set(view)）
    w.__mpEnter = () => setMp(true);     // 强制进入多人子应用（不经真连接）
    w.__mpExit = () => setMp(false);     // 退出多人子应用
    w.__mpPushToast = (text: string, kind?: 'info' | 'success' | 'warning' | 'error') =>
      pushToast(text, kind ?? 'info');   // 触发一条 toast（验证让位/静默）
    return () => { delete w.__mpEnter; delete w.__mpExit; delete w.__mpPushToast; };
  }, []);

  /*
   * BUG-R18-01 自愈：PWA 可能仍在运行历史旧包（旧包连不上房间服务，稳定报「连接服务器失败」）。
   * 启动时比对「已加载入口指纹 vs 服务端最新入口指纹」，发现陈旧则清缓存并自动重载一次。
   */
  useEffect(() => { void autoHealStaleBuild(); }, []);

  useEffect(() => {
    applySettingsToDom(settings);
  }, [settings]);

  // 离开页面前尽力保存一次（每手结束本来就会写档，这里覆盖「手牌中途关页」）
  useEffect(() => {
    const onHide = (): void => {
      if (controller.game && controller.game.phase !== 'gameOver') controller.persist();
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  const start = useCallback((p: SetupParams) => {
    setLastSetup(p);
    controller.startNewGame(p);
    setSave(loadDoc());
  }, []);

  const resume = useCallback(() => {
    const r = loadDoc();
    if (r.status !== 'ok') {
      pushToast('存档不可用，请开始新对局', 'warning');
      setSave(r);
      return;
    }
    const ok = controller.resumeFromDoc(r.doc);
    if (ok) pushToast(`已恢复到第 ${r.doc.engine.handNumber} 手`, 'success');
    setSave(loadDoc());
  }, []);

  const home = useCallback(() => {
    setSave(loadDoc());
  }, []);

  return (
    <>
      {mp ? (
        <MpApp onExit={() => setMp(false)} />
      ) : controller.game ? (
        <GameScreen c={controller} settings={settings} lastSetup={lastSetup} onHome={home} onRestart={start} />
      ) : (
        <StartScreen
          save={save}
          onStart={start}
          onResume={resume}
          onEnterMultiplayer={() => { prewarmWorker(); setMp(true); }}
          onDiscardSave={() => {
            controller.abandonSave();
            setSave(loadDoc());
            pushToast('存档已清除', 'info');
          }}
        />
      )}
      {/* FR-B（AC-B.2）：牌桌在场时 toast 由 GameScreen 的 overlay 根渲染（z:115，随结算静默排队）；
          仅在 StartScreen / 多人（GameScreen 不在树上）时由 App 根挂载 ToastLayer，避免双份渲染。 */}
      {mp || !controller.game ? <ToastLayer items={toasts} /> : null}
      <PwaHints />
    </>
  );
}
